import { useCallback, useState } from "react"

import {
  trackAppletCreationCompleted,
  trackConversationalUIAppletCreated,
  trackConversationalUISessionEnded,
  trackConversationalUISessionStarted,
  trackConversationalUITurnCompleted,
  type AppletCreationMode,
  type ConversationEntryPoint,
  type ConversationGoalType,
  type ConversationOutcome,
} from "app/utils/mixpanel_tracking"

import type { User } from "../utils/interfaces"
import Conversation from "./conversation"
import { recordCreatedApplet } from "./created_applet_storage"
import Intro from "./intro"
import { isCreateAppletSuccessResult } from "./sse_events"
import type {
  AppletCreatedMetadata,
  SessionEndedMetadata,
  SessionStartedMetadata,
  TurnCompletedMetadata,
  UpgradeRequest,
} from "./types"

interface Props {
  active: boolean
  showUpgradeModal: (request?: UpgradeRequest) => void
  trackCreationStarted: (creationMode: AppletCreationMode) => void
  creationAttemptId: () => string
  creationAttemptMode: () => AppletCreationMode
  user: User
}

const ENTRY_POINT: ConversationEntryPoint = "create_applet"
const GOAL_TYPE: ConversationGoalType = "create_applet"

function deriveOutcome(metadata: SessionEndedMetadata, active: boolean): ConversationOutcome {
  if (metadata.goalAchieved) return "success_goal_achieved"
  if (metadata.errorBlocking) return "error_blocking"
  if (!active) return "redirected_to_classic"
  return "abandoned_mid_flow"
}

const ChatTab = (props: Props) => {
  const [stage, setStage] = useState<"intro" | "conversation">("intro")
  const [seedMessage, setSeedMessage] = useState("")

  const { trackCreationStarted, creationAttemptId, creationAttemptMode } = props

  const handleIntroSubmit = useCallback(
    (prompt: string) => {
      trackCreationStarted("ai")
      setSeedMessage(prompt)
      setStage("conversation")
    },
    [trackCreationStarted],
  )

  const handleAppletCreated = useCallback(
    (applet: unknown, metadata: AppletCreatedMetadata) => {
      if (isCreateAppletSuccessResult(applet)) {
        trackConversationalUIAppletCreated({
          conversationId: metadata.conversationId,
          appletId: applet.applet_slug,
          promptsToCreation: metadata.promptsToCreation,
          conversationDurationSeconds: metadata.conversationDurationSeconds,
        })
        trackAppletCreationCompleted({
          creationMode: creationAttemptMode(),
          creationAttemptId: creationAttemptId(),
          entrypoint: "diy_composer",
          appletId: applet.applet_slug,
        })
        recordCreatedApplet({ conversationId: metadata.conversationId, appletId: applet.applet_slug })
      }
    },
    [creationAttemptId, creationAttemptMode],
  )

  const handleSessionStarted = useCallback((metadata: SessionStartedMetadata) => {
    trackConversationalUISessionStarted({
      conversationId: metadata.conversationId,
      goalType: GOAL_TYPE,
      entryPoint: ENTRY_POINT,
      sessionStartTimestamp: metadata.startedAtIso,
    })
  }, [])

  const handleSessionEnded = useCallback(
    (metadata: SessionEndedMetadata) => {
      trackConversationalUISessionEnded({
        conversationId: metadata.conversationId,
        totalUserPrompts: metadata.totalUserPrompts,
        totalAiResponses: metadata.totalAiResponses,
        conversationOutcome: deriveOutcome(metadata, props.active),
        goalAchieved: metadata.goalAchieved,
        exitMethod: metadata.exitMethod,
        sessionEndTimestamp: metadata.endedAtIso,
        sessionDurationSeconds: metadata.durationSeconds,
        viaBeacon: metadata.viaBeacon,
      })
    },
    [props.active]
  )

  const handleTurnCompleted = useCallback((metadata: TurnCompletedMetadata) => {
    trackConversationalUITurnCompleted({
      conversationId: metadata.conversationId,
      turnNumber: metadata.turnNumber,
      responseTime: metadata.responseTimeMs,
      userActionAfterResponse: metadata.userAction,
      errorOccurred: metadata.errorOccurred,
      mcpToolCalled: metadata.mcpToolCalled,
      mcpToolSuccess: metadata.mcpToolSuccess,
    })
  }, [])

  if (stage === "conversation") {
    return (
      <Conversation
        initialMessage={seedMessage}
        intention="ifttt_assistant"
        onAppletCreated={handleAppletCreated}
        onSessionStarted={handleSessionStarted}
        onSessionEnded={handleSessionEnded}
        onTurnCompleted={handleTurnCompleted}
        username={props.user.login}
        onUpgradeRequest={props.showUpgradeModal}
      />
    )
  }

  return <Intro active={props.active} user={props.user} onSubmit={handleIntroSubmit} />
}

export default ChatTab
